import React, { useEffect, useState } from 'react';
import api from '../../../apiManager/api'; // Adjust path
import { manualCapture } from '../../../apiManager/admin/JobsManagement'; // Adjust path
import { cancelJob, cancelJobByAdmin } from '../../../apiManager/houseOwner/matchShvoller'; // Adjust path
import { formatPayment, formatScheduledDate } from '../../../lib/utils'; // Adjust path

// UI Components
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../../components/ui/table";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../../components/ui/dialog";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "../../../components/ui/popover";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Card, CardContent } from "../../../components/ui/card";
import { Separator } from "../../../components/ui/separator";

// Icons
import { 
    ChevronDown, ChevronUp, Check, X, User, Info, ToolCase, Filter, RefreshCw,
    DollarSign, MessageSquare, ShieldAlert, Ban, CreditCard, Loader2
} from 'lucide-react';
import { FaStripe } from "react-icons/fa";
import LocationAddress from "../../../components/app/shared/LocationComponent";

// --- Configuration for Filters ---
const FILTER_CONFIG = [
    {
        key: 'jobStatus',
        label: 'Job Status',
        options: [
            { label: 'Open', value: 'open' },
            { label: 'Scheduled', value: 'scheduled' },
            { label: 'In Progress', value: 'in-progress' },
            { label: 'Completed', value: 'completed' },
            { label: 'Not Anymore', value: 'not-anymore' },
        ]
    },
    {
        key: 'paymentInfo.status',
        label: 'Payment Status',
        options: [
            { label: 'Pending', value: 'pending' },
            { label: 'Authorized', value: 'authorize' },
            { label: 'Captured', value: 'capture' },
            { label: 'Canceled', value: 'canceled' },
        ]
    },
    {
        key: 'window',
        label: 'Service Window',
        options: [
            { label: '2 Hours', value: '2h' },
            { label: '3 Hours', value: '3h' },
            { label: '4 Hours', value: '4h' },
            { label: '5 Hours', value: '5h' },
        ]
    },
    {
        key: 'isRequestedForCancel',
        label: 'Cancel Request',
        options: [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
        ]
    }
];

// --- Helper: Truncated Text ---
const TruncatedText = ({ text, maxLength = 30, label = "Details" }) => {
    if (!text) return <span className="text-gray-400 italic">N/A</span>;
    const str = String(text);
    if (str.length <= maxLength) return <span>{str}</span>;
    return (
        <Popover>
            <PopoverTrigger asChild>
                <span className="cursor-pointer text-blue-600 hover:underline flex items-center gap-1">
                    {str.substring(0, maxLength)}...
                    <Info className="w-3 h-3" />
                </span>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4 text-sm bg-white shadow-xl border border-gray-200 z-50">
                <h4 className="font-semibold mb-2 text-xs uppercase text-gray-500">{label}</h4>
                <p className="whitespace-pre-wrap text-slate-700">{str}</p>
            </PopoverContent>
        </Popover>
    );
};

// --- Main Component ---
const StripeJobs = () => {
    // Data States
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    
    // Filter State
    const [activeFilters, setActiveFilters] = useState({});

    // UI States
    const [expandedRows, setExpandedRows] = useState(new Set());
    
    // Payment Dialog State
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
    const [fetchingStripeId, setFetchingStripeId] = useState(null);

    // Chat State
    const [chatData, setChatData] = useState([]);
    const [isChatDialogOpen, setIsChatDialogOpen] = useState(false);
    const [loadingChat, setLoadingChat] = useState(false);

    // Action Confirmation State (Capture/Cancel)
    const [actionDialog, setActionDialog] = useState({ open: false, type: null, job: null });
    const [actionLoading, setActionLoading] = useState(false);

    const limit = 10;

    // --- API Call: Fetch Jobs ---
    const fetchJobs = async () => {
        setLoading(true);
        try {
            const response = await api.post(`/job/getJobsWithAdvancedFilters`, {
                page,
                limit,
                filters: activeFilters 
            });
            
            if (response.data) {
                setJobs(response.data.jobs);
                setTotalPages(response.data.totalPages);
            }
        } catch (error) {
            console.error("Error fetching jobs:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJobs();
    }, [page, activeFilters]);

    // --- Filters Handlers ---
    const handleFilterSelect = (key, value) => {
        setActiveFilters(prev => ({ ...prev, [key]: value }));
        setPage(1);
    };

    const removeFilter = (key) => {
        const newFilters = { ...activeFilters };
        delete newFilters[key];
        setActiveFilters(newFilters);
        setPage(1);
    };

    const clearAllFilters = () => {
        setActiveFilters({});
        setPage(1);
    };

    const toggleRow = (jobId) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(jobId)) newExpanded.delete(jobId);
        else newExpanded.add(jobId);
        setExpandedRows(newExpanded);
    };

    // --- Payment Handler ---
    const handleFetchStripeInfo = async (job) => {
        if (!job.paymentIntentId) return;
        setFetchingStripeId(job._id);
        try {
            const response = await api.post(`/job/fetchStripeDetails?paymentIntent=${job.paymentIntentId}`);
            setSelectedPayment(response.data);
            setIsPaymentDialogOpen(true);
        } catch (error) {
            console.error("Error fetching stripe details:", error);
        } finally {
            setFetchingStripeId(null);
        }
    };

    // --- Chat Handler ---
    const handleViewChat = async (jobId) => {
        setLoadingChat(true);
        try {
            const url = "/job/admin/getchats/" + jobId;
            const res = await api.get(url);
            const { data } = res.data;
            if (!data || data.length === 0) {
                alert("No chat history found for this job.");
            } else {
                setChatData(data);
                setIsChatDialogOpen(true);
            }
        } catch (error) {
            console.error("Error fetching chat:", error);
            alert("Failed to fetch chat logs.");
        } finally {
            setLoadingChat(false);
        }
    };

    // --- Action Handlers (Cancel/Capture) ---
    const openActionDialog = (type, job) => {
        setActionDialog({ open: true, type, job });
    };

    const performAction = async () => {
        if (!actionDialog.job) return;

        const job = actionDialog.job;
        const jobId = job._id;
        // Safely get shoveler ID
        const shovelerId = job.ShovelerInfo?.[0]?.ShovelerId?._id;

        // Validation for actions requiring a shoveler
        if ((actionDialog.type !== 'cancel-admin' && actionDialog.type !== 'cancel-shoveler') && !shovelerId) {
             // Technically capture needs shovelerId in the backend logic provided? 
             // If manualCapture requires shovelerId, we need it. 
             // Assuming capture needs it based on previous code: manualCapture(jobId, shovelerId, 'admin')
             if (actionDialog.type === 'capture' && !shovelerId) {
                alert("No shoveler assigned to this job. Cannot capture.");
                return;
             }
        }

        setActionLoading(true);
        try {
            if (actionDialog.type === 'capture') {
                const res = await manualCapture(jobId, shovelerId, 'admin');
                if (res && res.error) {
                    alert(res.error);
                } else {
                    alert('Payment Captured Successfully');
                    fetchJobs(); // Refresh data
                }
            } else if (actionDialog.type === 'cancel-shoveler') {
                const res = await cancelJob(jobId, shovelerId);
                if (res.err) alert(res.err);
                else {
                    alert(res.message || "Job cancelled as shoveler successfully.");
                    fetchJobs();
                }
            } else if (actionDialog.type === 'cancel-admin') {
                const res = await cancelJobByAdmin(jobId, shovelerId); // ShovelerId might be null, backend should handle or we send null
                if (res.err) alert(res.err);
                else {
                    alert(res.message || "Job cancelled by admin successfully.");
                    fetchJobs();
                }
            }
        } catch (error) {
            console.error("Action failed:", error);
            alert(error.message || "An error occurred.");
        } finally {
            setActionLoading(false);
            setActionDialog({ open: false, type: null, job: null });
        }
    };

    // --- Formatters ---
    const formatDate = (ts) => ts ? new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', year: '2-digit', hour: '2-digit', minute:'2-digit' }) : 'N/A';
    
    const getStatusColor = (status) => {
        switch (status) {
            case 'open': return 'bg-blue-50 text-blue-700 border-blue-200';
            case 'scheduled': return 'bg-amber-50 text-amber-700 border-amber-200';
            case 'in-progress': return 'bg-purple-50 text-purple-700 border-purple-200';
            case 'completed': return 'bg-green-50 text-green-700 border-green-200';
            case 'canceled': return 'bg-red-50 text-red-700 border-red-200';
            case 'not-anymore': return 'bg-gray-50 text-gray-500 border-gray-200';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    // --- Render Expanded Content ---
    const renderExpandedContent = (job) => (
        <div className="p-4 bg-slate-50/50 rounded-b-lg border-t border-slate-100 shadow-inner">
             <div className="grid grid-cols-4 gap-6 text-sm">
                
                {/* --- Column 1: People --- */}
                <div className="space-y-4">
                    <h4 className="flex items-center gap-2 font-bold text-slate-800 uppercase text-xs tracking-wider">
                        <User size={14} /> Stakeholders
                    </h4>
                    
                    {/* Homeowner */}
                    <Card className="shadow-sm">
                        <CardContent className="p-3 flex items-start gap-3">
                            <div className="h-10 w-10 rounded-full bg-slate-200 overflow-hidden flex-shrink-0">
                                {job.houseOwnerId?.imageUrl ? (
                                    <img src={job.houseOwnerId.imageUrl} alt="Owner" className="h-full w-full object-cover"/>
                                ) : (
                                    <div className="h-full w-full flex items-center justify-center text-slate-400 font-bold">
                                        {job.houseOwnerId?.userName?.[0] || "?"}
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col min-w-0">
                                <span className="font-semibold text-slate-900 truncate">{job.houseOwnerId?.userName || "Unknown"}</span>
                                <span className="text-xs text-slate-500 font-mono truncate max-w-[120px]" title={job.houseOwnerId?._id}>ID: {job.houseOwnerId?._id}</span>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Shovellers */}
                    <div className="space-y-2">
                        <span className="text-xs font-semibold text-slate-500 ml-1">Assigned Shovelers</span>
                        {job.ShovelerInfo?.length > 0 ? job.ShovelerInfo.map((info, idx) => (
                            <Card key={idx} className="shadow-sm border-l-4 border-l-blue-500">
                                <CardContent className="p-2 text-xs">
                                    <div className="flex justify-between mb-1">
                                        <span className="font-bold text-slate-800">{info.ShovelerId?.firstName} {info.ShovelerId?.lastName}</span>
                                        <Badge variant="secondary" className="text-[10px] h-4 px-1">{info.shovellerAction}</Badge>
                                    </div>
                                    <div className="text-slate-500 truncate">{info.ShovelerId?.email}</div>
                                    <div className="mt-1 flex gap-2 text-[10px] text-slate-400">
                                        <span>Payout: {info.PayoutStatus || 'N/A'}</span>
                                    </div>
                                </CardContent>
                            </Card>
                        )) : <div className="text-xs text-slate-400 italic pl-1">No shovelers assigned</div>}
                    </div>
                </div>

                {/* --- Column 2: Service Detail --- */}
                <div className="space-y-4">
                    <h4 className="flex items-center gap-2 font-bold text-slate-800 uppercase text-xs tracking-wider">
                        <ToolCase size={14} /> Service Scope
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                        <div className="flex flex-col gap-1 p-2 bg-white rounded border">
                            <span className="text-xs text-slate-500 uppercase">Service Type</span>
                            <span className="font-medium">{job.serviceDetail?.service}</span>
                        </div>
                        <div className="flex flex-col gap-1 p-2 bg-white rounded border">
                            <span className="text-xs text-slate-500 uppercase">Window</span>
                            <span className="font-medium">{job.window}</span>
                        </div>
                        <div className="flex flex-col gap-1 p-2 bg-white rounded border">
                            <span className="text-xs text-slate-500 uppercase">Instructions</span>
                            <TruncatedText text={job.serviceDetail?.serviceInstruction?.join('. ')} maxLength={60} label="Full Instructions" />
                        </div>
                        <div className="flex flex-col gap-1 p-2 bg-white rounded border">
                            <span className="text-xs text-slate-500 uppercase">Tools</span>
                            <span className="font-medium">{job.serviceDetail?.serviceEquipment?.join(', ') || 'N/A'}</span>
                        </div>
                    </div>
                </div>

                {/* --- Column 3: Financials --- */}
                <div className="space-y-4">
                    <h4 className="flex items-center gap-2 font-bold text-slate-800 uppercase text-xs tracking-wider">
                        <DollarSign size={14} /> Financials
                    </h4>
                    <div className="bg-white p-3 rounded-lg border shadow-sm space-y-3">
                        <div className="flex justify-between items-center pb-2 border-b border-dashed">
                            <span className="text-slate-500">Total Amount</span>
                            <span className="font-bold text-lg">{formatPayment(job.paymentInfo?.amount || 0, {is80:false})}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-slate-500">Payment Status</span>
                            <Badge className={job.paymentInfo?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}>
                                {job.paymentInfo?.status}
                            </Badge>
                        </div>
                    </div>

                    <div className="bg-slate-100 p-3 rounded-lg border border-slate-200">
                        <div className="text-[10px] font-mono text-slate-500 mb-3 break-all">
                            <span className="font-bold block mb-1">Payment Intent:</span>
                            {job.paymentIntentId || 'N/A'}
                        </div>
                        <Button 
                            className="w-full h-8 text-xs bg-[#635BFF] hover:bg-[#5851df] text-white flex gap-2 items-center justify-center"
                            disabled={!job.paymentIntentId || fetchingStripeId === job._id}
                            onClick={() => handleFetchStripeInfo(job)}
                        >
                            {fetchingStripeId === job._id ? <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" /> : <FaStripe size={16} />}
                            {fetchingStripeId === job._id ? 'Fetching...' : 'Verify on Stripe'}
                        </Button>
                    </div>
                </div>

                {/* --- Column 4: Actions & Meta --- */}
                <div className="space-y-4">
                    <h4 className="flex items-center gap-2 font-bold text-slate-800 uppercase text-xs tracking-wider">
                        <Info size={14} /> Actions & Meta
                    </h4>
                    
                    {/* Meta Info */}
                    <div className="text-[11px] space-y-1 text-slate-500 bg-white p-2 rounded border">
                        <div className="flex justify-between">
                            <span>Created:</span>
                            <span className="font-medium text-slate-700">{formatDate(job.createdAt)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Requested:</span>
                            <span className="font-medium text-slate-700">{formatDate(job.ShovelerInfo?.[0]?.requestedTime)}</span>
                        </div>
                         <div className="flex justify-between">
                            <span>HO Comp:</span>
                            <span className="font-medium text-slate-700">{formatDate(job.ShovelerInfo?.[0]?.houseOwnerCompletedDate)}</span>
                        </div>
                    </div>

                    <Separator className="my-2"/>

                    {/* Admin Actions Grid */}
                    <div className="grid grid-cols-2 gap-2">
                        {/* Chat Button */}
                        <Button 
                            variant="outline" 
                            size="sm"
                            className="w-full text-xs h-8 flex items-center justify-center gap-1.5"
                            onClick={() => handleViewChat(job._id)}
                            disabled={loadingChat}
                        >
                            {loadingChat ? <Loader2 size={12} className="animate-spin"/> : <MessageSquare size={12} />}
                            View Chat
                        </Button>

                        {/* Capture Payment */}
                        <Button 
                            variant="secondary"
                            size="sm"
                            className="w-full text-xs h-8 flex items-center justify-center gap-1.5 bg-slate-800 text-white hover:bg-slate-700"
                            onClick={() => openActionDialog('capture', job)}
                        >
                            <CreditCard size={12} />
                            Capture Pay
                        </Button>

                        {/* Cancel Shoveller */}
                        <Button 
                            variant="destructive" 
                            size="sm"
                            className="w-full text-xs h-8 flex items-center justify-center gap-1.5 bg-orange-600 hover:bg-orange-700"
                            onClick={() => openActionDialog('cancel-shoveler', job)}
                            disabled={job.jobStatus !== 'in-progress'}
                            title={job.jobStatus !== 'in-progress' ? "Job must be In Progress" : ""}
                        >
                            <Ban size={12} />
                            Cancel (S)
                        </Button>

                        {/* Cancel Admin */}
                        <Button 
                            variant="destructive" 
                            size="sm"
                            className="w-full text-xs h-8 flex items-center justify-center gap-1.5"
                            onClick={() => openActionDialog('cancel-admin', job)}
                            disabled={job.jobStatus !== 'in-progress'}
                            title={job.jobStatus !== 'in-progress' ? "Job must be In Progress" : ""}
                        >
                            <ShieldAlert size={12} />
                            Cancel (A)
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );

    // --- Render Payment Dialog (Clean Version) ---
    const renderPaymentDialog = () => {
        if (!selectedPayment) return null;

        const { id, amount, status, capture_method, amount_received, latest_charge, currency } = selectedPayment;

        // Calculations based on latest_charge
        const captured = latest_charge?.amount_captured || 0;
        const refunded = latest_charge?.amount_refunded || 0;
        const netSpent = captured - refunded;

        return (
            <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
                <DialogContent className="sm:max-w-[500px] bg-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><FaStripe className="text-[#635BFF]"/> Stripe Payment Details</DialogTitle>
                        <DialogDescription className="font-mono text-xs">
                            Detailed information for Payment Intent: {id}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex flex-col space-y-1">
                                <span className="text-sm font-medium text-slate-500">Status</span>
                                <span className={`text-sm font-semibold capitalize ${status === 'succeeded' ? 'text-green-600' : 'text-yellow-600'}`}>
                                    {status}
                                </span>
                            </div>
                            <div className="flex flex-col space-y-1">
                                <span className="text-sm font-medium text-slate-500">Capture Method</span>
                                <span className="text-sm capitalize text-slate-800">{capture_method?.replace('_', ' ')}</span>
                            </div>
                        </div>

                        <div className="border-t pt-4">
                            <h4 className="mb-3 text-sm font-semibold text-gray-900">Financial Breakdown ({currency?.toUpperCase()})</h4>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Total Authorized Amount</span>
                                    <span className="text-sm font-medium">${(amount / 100).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Amount Received</span>
                                    <span className="text-sm font-medium text-green-600">${(amount_received / 100).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Amount Captured</span>
                                    <span className="text-sm font-medium">${(captured / 100).toFixed(2)}</span>
                                </div>
                                {refunded > 0 && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-red-600">Amount Refunded</span>
                                        <span className="text-sm font-medium text-red-600">-${(refunded / 100).toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="border-t border-dashed pt-2 mt-2 flex justify-between items-center">
                                    <span className="text-sm font-bold text-gray-900">Net Spent</span>
                                    <span className="text-sm font-bold text-gray-900">${(netSpent / 100).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                        {latest_charge && (
                            <div className="bg-gray-50 p-3 rounded-md text-xs text-gray-500 mt-2 border border-gray-200">
                                <p><span className="font-semibold">Latest Charge ID:</span> {latest_charge.id}</p>
                                <p><span className="font-semibold">Method:</span> {latest_charge.payment_method_details?.type} - {latest_charge.payment_method_details?.card?.brand} •••• {latest_charge.payment_method_details?.card?.last4}</p>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        );
    };

    // --- Render Chat Dialog ---
    const renderChatDialog = () => (
        <Dialog open={isChatDialogOpen} onOpenChange={setIsChatDialogOpen}>
            <DialogContent className="sm:max-w-[500px] bg-white max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><MessageSquare size={18}/> Job Chat Log</DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto p-4 bg-slate-50 rounded-md border border-slate-100 space-y-4">
                    {chatData.map((msg, i) => (
                        <div key={i} className={`flex flex-col ${msg.sender === 'System' ? 'items-center' : 'items-start'}`}>
                            <div className={`p-3 rounded-lg text-sm max-w-[90%] ${msg.sender === 'System' ? 'bg-gray-200 text-gray-600 text-xs italic' : 'bg-white border shadow-sm'}`}>
                                <span className="font-bold text-xs block text-slate-400 mb-1">{msg.sender}</span>
                                <p className="text-slate-800">{msg.message}</p>
                            </div>
                            <span className="text-[10px] text-slate-400 mt-1 pl-1">
                                {new Date(msg.timestamp).toLocaleString()}
                            </span>
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );

    // --- Render Action Confirmation Dialog ---
    const renderActionDialog = () => {
        let title = "";
        let description = "";
        let confirmBtnClass = "";

        if (actionDialog.type === 'capture') {
            title = "Capture Payment";
            description = "Are you sure you want to capture the payment for this job? This will process the funds.";
            confirmBtnClass = "bg-slate-900 hover:bg-slate-800";
        } else if (actionDialog.type === 'cancel-shoveler') {
            title = "Cancel as Shoveller";
            description = "Are you sure you want to cancel this job on behalf of the shoveller? This action might have penalties.";
            confirmBtnClass = "bg-orange-600 hover:bg-orange-700";
        } else if (actionDialog.type === 'cancel-admin') {
            title = "Permanently Cancel Job";
            description = "Are you sure you want to permanently cancel this job as an Admin? This action cannot be undone.";
            confirmBtnClass = "bg-red-600 hover:bg-red-700";
        }

        return (
            <Dialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog(prev => ({ ...prev, open: false }))}>
                <DialogContent className="sm:max-w-[400px] bg-white">
                    <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>{description}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null, job: null })}>
                            Cancel
                        </Button>
                        <Button 
                            className={`text-white ${confirmBtnClass}`} 
                            onClick={performAction} 
                            disabled={actionLoading}
                        >
                            {actionLoading ? <Loader2 size={16} className="animate-spin mr-2"/> : null}
                            Confirm
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    };

    return (
        <div className="w-full min-h-screen bg-white p-8 font-sans">
            {/* --- Header Section --- */}
            <div className="flex flex-col gap-6 mb-8">
                <div className="flex justify-between items-start">
                    <div>
                        <h2 className="text-3xl font-bold tracking-tight text-slate-900">Job Management</h2>
                        <p className="text-slate-500 text-sm mt-1">Review jobs, payments, and statuses.</p>
                    </div>
                    {/* Pagination */}
                    <div className="flex items-center gap-3 bg-white border rounded-md shadow-sm p-1">
                        <Button variant="ghost" size="icon" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="h-8 w-8">
                            <ChevronDown className="rotate-90 h-4 w-4" />
                        </Button>
                        <span className="text-sm font-medium w-20 text-center">Page {page}/{totalPages}</span>
                        <Button variant="ghost" size="icon" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="h-8 w-8">
                            <ChevronDown className="-rotate-90 h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* --- Filter Bar --- */}
                <div className="flex flex-wrap items-center gap-3 p-4 bg-slate-50/50 border rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mr-2">
                        <Filter size={16} />
                        <span className="font-semibold">Filters:</span>
                    </div>

                    {FILTER_CONFIG.map((conf) => {
                        const isActive = activeFilters[conf.key] !== undefined;
                        const currentLabel = isActive 
                            ? conf.options.find(o => o.value === activeFilters[conf.key])?.label 
                            : conf.label;

                        return (
                            <Popover key={conf.key}>
                                <PopoverTrigger asChild>
                                    <button
                                        className={`
                                            flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all border
                                            ${isActive 
                                                ? 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200' 
                                                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:shadow-sm'}
                                        `}
                                    >
                                        {isActive && <Check size={12} />}
                                        {isActive ? `${conf.label}: ${currentLabel}` : conf.label}
                                        <ChevronDown size={12} className="opacity-50"/>
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-56 p-2 bg-white" align="start">
                                    <div className="space-y-1">
                                        <h4 className="text-xs font-semibold text-slate-400 px-2 py-1 uppercase">{conf.label}</h4>
                                        {conf.options.map((opt) => (
                                            <div 
                                                key={opt.value}
                                                onClick={() => handleFilterSelect(conf.key, opt.value)}
                                                className={`
                                                    cursor-pointer px-2 py-1.5 rounded text-sm flex justify-between items-center
                                                    ${activeFilters[conf.key] === opt.value ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-100 text-slate-700'}
                                                `}
                                            >
                                                {opt.label}
                                                {activeFilters[conf.key] === opt.value && <Check size={14} />}
                                            </div>
                                        ))}
                                        {isActive && (
                                            <>
                                                <Separator className="my-1"/>
                                                <div 
                                                    onClick={() => removeFilter(conf.key)}
                                                    className="cursor-pointer px-2 py-1.5 rounded text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                >
                                                    <X size={12} /> Clear Filter
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </PopoverContent>
                            </Popover>
                        );
                    })}

                    {Object.keys(activeFilters).length > 0 && (
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={clearAllFilters}
                            className="text-xs text-slate-500 hover:text-red-600 ml-auto"
                        >
                            Clear All
                        </Button>
                    )}
                    
                    <Button variant="outline" size="icon" className="h-8 w-8 ml-2 bg-white" onClick={fetchJobs} title="Refresh">
                        <RefreshCw size={14} className={loading ? "animate-spin" : ""}/>
                    </Button>
                </div>
            </div>

            {/* --- Main Table --- */}
            <div className="rounded-md border shadow-sm bg-white">
                <Table>
                    <TableHeader className="bg-slate-50">
                        <TableRow>
                            <TableHead className="w-[100px] font-bold">Job ID</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Location</TableHead>
                            <TableHead>Posted Date</TableHead>
                            <TableHead>Scheduled / Window</TableHead>
                            <TableHead className="w-[50px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} className="h-64 text-center text-slate-500">
                                    <div className="flex flex-col items-center justify-center gap-2">
                                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-500"></div>
                                        <span className="text-xs">Loading jobs data...</span>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : jobs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="h-48 text-center text-slate-500 italic">
                                    No jobs found matching these filters.
                                </TableCell>
                            </TableRow>
                        ) : (
                            jobs.map((job) => (
                                <React.Fragment key={job._id}>
                                    <TableRow 
                                        className={`cursor-pointer transition-colors hover:bg-slate-50 ${expandedRows.has(job._id) ? 'bg-slate-50/80 border-b-0' : ''}`}
                                        onClick={() => toggleRow(job._id)}
                                    >
                                        <TableCell className="font-mono text-xs font-medium text-slate-600">
                                            #{job._id.slice(-6).toUpperCase()}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={`capitalize font-semibold shadow-none ${getStatusColor(job.jobStatus)}`}>
                                                {job.jobStatus}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-sm text-slate-600">
                                            {job.location?.coordinates ? (
                                                <LocationAddress longitude={job.location.coordinates[0]} latitude={job.location.coordinates[1]} />
                                            ) : 'N/A'}
                                        </TableCell>
                                        <TableCell className="text-sm text-slate-600">
                                            {formatDate(job.createdAt)}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            <div className="flex flex-col">
                                                {job.scheduledDate ? (
                                                    <>
                                                        <span className="font-medium text-slate-700">{formatScheduledDate(job.scheduledDate)}</span>
                                                        <span className="text-xs text-slate-500">
                                                            {job.scheduledTime?.hour}:{job.scheduledTime?.minute} {job.scheduledTime?.period}
                                                        </span>
                                                    </>
                                                ) : <span className="text-slate-400 text-xs italic">Not Scheduled</span>}
                                                <span className="text-[10px] text-slate-400 bg-slate-100 w-fit px-1 rounded mt-1">Window: {job.window}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {expandedRows.has(job._id) ? <ChevronUp size={16} className="text-slate-400"/> : <ChevronDown size={16} className="text-slate-400"/>}
                                        </TableCell>
                                    </TableRow>
                                    
                                    {/* Expanded Row Content */}
                                    {expandedRows.has(job._id) && (
                                        <TableRow className="hover:bg-transparent">
                                            <TableCell colSpan={6} className="p-0 border-t-0">
                                                {renderExpandedContent(job)}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
            
            {/* Dialogs */}
            {renderPaymentDialog()}
            {renderChatDialog()}
            {renderActionDialog()}
        </div>
    );
};

export default StripeJobs;